import { useState, type FormEvent } from "react";
import { useSignUp } from "@clerk/react";
import { Btn, Eyebrow, Section } from "@championcare/ui";
import { CLERK_ENABLED, patientOrigin, patientSignInUrl, patientSignupUrl } from "../config";

// Real Clerk account creation on the marketing site, restyled: the stock <SignUp>
// widget is replaced by our own form running Clerk headlessly (signUp.password → email code →
// finalize), so the page matches the prototype design exactly and never links out to the hosted
// accounts.dev portal. No PHI is collected here (D5); the chosen state/tier ride unsafeMetadata for
// the provisioning webhook. After signup the new patient lands in the portal, which shows the
// welcome toast (first name carried on the ?welcome= hand-off param — sessionStorage doesn't cross
// origins). When no Clerk key is configured the page degrades to the portal hand-off link.

const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

function selectedTier(): "basic" | "premium" | undefined {
  const t = new URLSearchParams(window.location.search).get("tier");
  return t === "basic" || t === "premium" ? t : undefined;
}

function clerkMessage(error: unknown): string {
  const e = error as { longMessage?: string; message?: string } | null;
  return e?.longMessage ?? e?.message ?? "Something went wrong — please try again.";
}

const GoogleIcon = (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

const AppleIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.05 12.04c-.03-2.65 2.16-3.92 2.26-3.98-1.23-1.8-3.15-2.05-3.83-2.08-1.63-.16-3.18.96-4.01.96-.83 0-2.1-.94-3.45-.91-1.78.03-3.42 1.03-4.33 2.62-1.85 3.2-.47 7.94 1.33 10.54.88 1.27 1.93 2.7 3.3 2.65 1.32-.05 1.82-.85 3.42-.85 1.6 0 2.05.85 3.45.83 1.42-.03 2.33-1.3 3.2-2.58 1-1.47 1.42-2.9 1.44-2.97-.03-.01-2.76-1.06-2.79-4.2zM14.5 4.2c.73-.88 1.22-2.11 1.08-3.33-1.05.04-2.32.7-3.07 1.58-.67.78-1.26 2.03-1.1 3.22 1.17.09 2.36-.6 3.09-1.47z" />
  </svg>
);

function ClerkSignupFlow() {
  const { signUp, fetchStatus } = useSignUp();
  const [step, setStep] = useState<"form" | "verify">("form");
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const busy = fetchStatus === "fetching";

  const landInPortal = (first: string) => {
    const url = new URL("/", patientOrigin());
    url.searchParams.set("welcome", first);
    window.location.href = url.toString();
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const [first, ...rest] = name.split(/\s+/);
    const state = String(data.get("state") ?? "");
    const tier = selectedTier();
    const created = await signUp.password({
      emailAddress: String(data.get("email") ?? "").trim(),
      password: String(data.get("password") ?? ""),
      firstName: first,
      lastName: rest.join(" ") || undefined,
      unsafeMetadata: { ...(tier ? { tier } : {}), ...(state ? { state } : {}) },
    });
    if (created.error) {
      setError(clerkMessage(created.error));
      return;
    }
    const sent = await signUp.verifications.sendEmailCode();
    if (sent.error) {
      setError(clerkMessage(sent.error));
      return;
    }
    setFirstName(first ?? "");
    setEmail(String(data.get("email") ?? "").trim());
    setStep("verify");
  };

  const verify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const code = String(new FormData(e.currentTarget).get("code") ?? "").trim();
    const attempted = await signUp.verifications.verifyEmailCode({ code });
    if (attempted.error) {
      setError(clerkMessage(attempted.error));
      return;
    }
    const finalized = await signUp.finalize();
    if (finalized.error) {
      setError(clerkMessage(finalized.error));
      return;
    }
    landInPortal(firstName);
  };

  const oauth = (strategy: "oauth_google" | "oauth_apple") => () => {
    const dest = new URL("/", patientOrigin());
    dest.searchParams.set("welcome", "");
    void signUp.sso({
      strategy,
      redirectUrl: dest.toString(),
      redirectCallbackUrl: `${window.location.origin}/sso-callback`,
    });
  };

  const errorLine = error ? (
    <p className="signup-error" role="alert">
      {error}
    </p>
  ) : null;

  if (step === "verify") {
    return (
      <div className="signup-card">
        <h2 className="signup-card__title">Check your email</h2>
        <p className="signup-card__sub">
          We sent a 6-digit code to <strong>{email}</strong>. Enter it below to create your account.
        </p>
        <form onSubmit={verify}>
          <div className="signup-field">
            <label htmlFor="su-code">Verification code</label>
            <input id="su-code" name="code" className="signup-input" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength={6} required autoFocus />
          </div>
          {errorLine}
          <button type="submit" className="btn btn-primary signup-submit" disabled={busy}>
            {busy ? "Verifying…" : "Verify & create account"}
          </button>
        </form>
        <p className="signup-alt">
          Didn&rsquo;t get it?{" "}
          <button
            type="button"
            className="signup-linkbtn"
            onClick={() => void signUp.verifications.sendEmailCode().then((r) => r.error && setError(clerkMessage(r.error)))}
            disabled={busy}
          >
            Resend code
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="signup-card">
      <form onSubmit={submit}>
        <div className="signup-field">
          <label htmlFor="su-name">Full name</label>
          <input id="su-name" name="name" className="signup-input" autoComplete="name" placeholder="Your name" required />
        </div>
        <div className="signup-field">
          <label htmlFor="su-email">Email</label>
          <input id="su-email" name="email" className="signup-input" type="email" autoComplete="email" placeholder="you@example.com" required />
        </div>
        <div className="signup-field">
          <label htmlFor="su-password">Password</label>
          <input id="su-password" name="password" className="signup-input" type="password" autoComplete="new-password" placeholder="At least 8 characters" required minLength={8} />
        </div>
        <div className="signup-field">
          <label htmlFor="su-state">Where do you live?</label>
          <select id="su-state" name="state" className="signup-input" defaultValue="" required>
            <option value="" disabled>
              Choose your state
            </option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <p className="signup-hint">We use this to match you with a licensed physician in your state.</p>
        </div>
        {errorLine}
        <button type="submit" className="btn btn-primary signup-submit" disabled={busy}>
          {busy ? "Creating…" : "Create account →"}
        </button>
      </form>

      <div className="signup-divider">OR</div>
      <button type="button" className="signup-oauth" onClick={oauth("oauth_google")}>
        {GoogleIcon} Continue with Google
      </button>
      <button type="button" className="signup-oauth" onClick={oauth("oauth_apple")}>
        {AppleIcon} Continue with Apple
      </button>

      <p className="signup-alt">
        Already have an account? <a href={patientSignInUrl()}>Sign in</a>
      </p>
    </div>
  );
}

export function Signup() {
  return (
    <Section className="signup signup--clerk">
      <div className="signup__head">
        <Eyebrow>Join ChampionCare</Eyebrow>
        <h1 className="display section__title">Premium telehealth, on your schedule.</h1>
        <p className="signup__lede">Get started in under a minute.</p>
      </div>

      <div className="signup__clerk">
        {CLERK_ENABLED ? (
          <ClerkSignupFlow />
        ) : (
          // Keyless fallback: no Clerk configured → hand off to the portal signup (its own account creation).
          <div className="signup__fallback">
            <p className="signup__lede">Account creation happens securely in the patient portal.</p>
            <Btn as="a" href={patientSignupUrl()} variant="primary">
              Continue to patient portal
            </Btn>
          </div>
        )}
      </div>
    </Section>
  );
}
